import type { SimpleContributionStatus, SimpleCostingSnapshot, SimpleCostStatus, SimpleIngredient, SimpleMarketPosition, SimpleProjection } from "../../lib/types";

export type SimpleRates = { spoilageRate: number; targetPercent: number; vatRate: number; serviceRate: number };

export function calculateSimpleCosting(ingredients: SimpleIngredient[], currentMenuPrice: number, rates: SimpleRates): SimpleCostingSnapshot {
  const itemCost = ingredients.reduce((total, ingredient) => total + positive(ingredient.lineCost), 0);
  const spoilageCost = itemCost * validRate(rates.spoilageRate);
  const ingredientAndSpoilageCost = itemCost + spoilageCost;
  const factor = 1 + validRate(rates.vatRate) + validRate(rates.serviceRate);
  const netRevenue = positive(currentMenuPrice) / factor;
  const actualIngredientCostPercent = netRevenue > 0 ? ingredientAndSpoilageCost / netRevenue : 0;
  const contribution = netRevenue - ingredientAndSpoilageCost;
  const contributionMarginPercent = netRevenue > 0 ? contribution / netRevenue : 0;
  const complete = ingredients.length > 0
    && ingredients.every(ingredient => ingredient.name.trim() && ingredient.amount.trim() && ingredient.lineCost > 0)
    && currentMenuPrice > 0;
  const targetPercent = rates.targetPercent > 0 && rates.targetPercent < 1 ? rates.targetPercent : .30;
  const status: SimpleCostStatus = !complete
    ? "Incomplete"
    : contribution <= 0
      ? "NotProfitable"
      : actualIngredientCostPercent > targetPercent
        ? "HighCost"
        : "Profitable";
  const contributionStatus: SimpleContributionStatus = !complete
    ? "Incomplete"
    : contribution <= 0
      ? "NotProfitable"
      : contributionMarginPercent < .30
        ? "LowMargin"
        : "Profitable";
  const projection = (target: number): SimpleProjection => {
    const requiredNetSellingPrice = ingredientAndSpoilageCost / target;
    const vatAmount = requiredNetSellingPrice * validRate(rates.vatRate);
    const serviceChargeAmount = requiredNetSellingPrice * validRate(rates.serviceRate);
    const exactAllInMenuPrice = requiredNetSellingPrice + vatAmount + serviceChargeAmount;
    return { targetPercent: target, requiredNetSellingPrice, vatAmount, serviceChargeAmount, exactAllInMenuPrice, suggestedMenuPrice: Math.ceil(exactAllInMenuPrice / 5) * 5 };
  };

  return {
    itemCost,
    spoilageCost,
    ingredientAndSpoilageCost,
    netRevenue,
    actualIngredientCostPercent,
    contribution,
    contributionMarginPercent,
    vatAmount: netRevenue * validRate(rates.vatRate),
    serviceChargeAmount: netRevenue * validRate(rates.serviceRate),
    status,
    contributionStatus,
    projections: [.20, .25, .30, .35].map(projection),
    targetProjection: projection(targetPercent)
  };
}

export function marketPosition(currentPrice: number, competitorPrices: number[]): SimpleMarketPosition {
  const prices = competitorPrices.filter(price => price > 0);
  if (currentPrice <= 0 || prices.length === 0) return "NoMarketData";
  if (currentPrice < Math.min(...prices)) return "BelowMarket";
  if (currentPrice > Math.max(...prices)) return "AboveMarket";
  return "WithinMarket";
}

const positive = (value: number) => Number.isFinite(value) && value > 0 ? value : 0;
const validRate = (value: number) => Number.isFinite(value) && value >= 0 && value < 1 ? value : 0;
